import React, { useEffect, useRef } from "react";
import { Scrollbars } from "react-custom-scrollbars-4";
import { useSnackbar } from "notistack";
import {
  Link,
  CircularProgress,
  Backdrop,
  Container,
  Stack,
  Breadcrumbs,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  List,
  Box,
  Collapse,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { getSchema } from "../helpers/schemas/getSchema";
import { getFormInvalid } from "../helpers/getFormInvalid";
import { checkInvalidField } from "../helpers/checkInvalidField";
import ImportDialog from "../Dialogs/ImportDialog";
import EditDialog from "../Dialogs/EditDialog";
// import { useLocation } from "react-router-dom";
import AddBoxIcon from "@mui/icons-material/AddBox";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ObservationCard from "./ObservationCard";
import { applyEditTrackingFields } from "../helpers/applyEditTrackingFields";
import { getExcelFeatures } from "../helpers/getExcelFeatures";
import setupEdit from "../helpers/setupEdit";
import mapSchema from "../helpers/mapSchema";
import { processFiles } from "../helpers/processFiles";
import { getSortArray } from "../helpers/getSortArray";
import { getCounts } from "../helpers/getCounts";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import SortDialog from "../Dialogs/SortDialog";
import { getObservationSchema } from "../helpers/schemas/getObservationSchema";
import { onEditSaveAll } from "../helpers/onEditSaveAll";
import { onEditDeleteAll } from "../helpers/onEditDeleteAll";
import { VisuallyHiddenInput } from "../helpers/VisuallyHiddenInput";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { getUserAccess } from "../helpers/getUserAccess";
import { Unpublished } from "@mui/icons-material";
import { getProject } from "../helpers/getProject";

export default function Observations(props) {
  const fileInputRef = useRef(null);
  const fileInputRef1 = useRef(null);
  const navigate = useNavigate();
  // const location = useLocation();
  // const { passProject } = location.state || {}; // Destructure and provide a default empty object
  // console.log(location.state);
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isReady, setIsReady] = React.useState(false);
  const [importDialogOpen, setImportDialogOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);

  const [project, setProject] = React.useState({});
  const [allObservations, setAllObservations] = React.useState([]);
  const [observations, setObservations] = React.useState([]);
  const [sortByValue, setSortByValue] = React.useState("No");
  const [sortByOptions] = React.useState([
    "No",
    "No Desc",
    "Date",
    "Date Desc",
    "Description",
    "Description Desc",
    "Type",
    "Type Desc",
  ]);
  const [importTitle, setImportTitle] = React.useState(
    "Observations Import > Choose Worksheet",
  );
  const [importStats, setImportStats] = React.useState([]);
  const [importSubtitle, setImportSubtitle] = React.useState(
    `Choose the worksheet with the data you wish to import.`,
  );
  const [importData, setImportData] = React.useState([]);
  const [importFields, setImportFields] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [filterValue, setFilterValue] = React.useState("");
  const [editFeature, setEditFeature] = React.useState(null);
  const [editFiles, setEditFiles] = React.useState([]);
  const [editSchema, setEditSchema] = React.useState([]);
  const [formErrorMessage, setformErrorMessage] = React.useState("");
  const [sheetName, setSheetName] = React.useState(null);
  const [workbook, setWorkBook] = React.useState({ SheetNames: [] });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = window.location.href.split("/");
        const proj = await getProject(params[params.length - 2]);

        setEditSchema(getSchema("observation"));
        setProject(proj);
        var obs = [...proj.Observations];
        obs = getCounts(
          obs,
          proj.Files,
          proj.Issues,
          proj.Comments,
          "observation_id",
        );
        setAllObservations(obs);
        onSortChange(sortByValue, obs);
      } catch (error) {
        console.error("Error getting document:", error);
        enqueueSnackbar(error.message, {
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
          variant: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (isLoading === true && isReady === false) {
      setIsReady(true);
      fetchData();
    }
  }); 

  const onDetailsClick = (item) => {
    if (item.focus.length > 0) {
      navigate(
        `/project/${project.id}/observation/${item.id}?focus=${item.focus}`,
        {
          state: { passProject: project },
        },
      );
    } else {
      navigate(`/project/${project.id}/observation/${item.id}`, {
        state: { passProject: project },
      });
    }
  };

  const onAddNew = () => {
    var item = {};
    var schema = getSchema("observation");
    schema.forEach((fd) => {
      item[fd.name] = "";
    });

    item["id"] = "new";
    // item["project_id"] = project["id"];
    item = applyEditTrackingFields(item, props.user);

    setEditSchema(schema);
    setEditFeature(item);
    setEditFiles([]);
    setImportTitle("New Observation");
    setEditDialogOpen(true);
  };

  const onEdit = (id) => {
    var item = observations.find((f) => f.id === id);
    item = { ...item };
    const results = setupEdit(item, "observation");
    const files = project.Files.filter((p) => p.observation_id === id);
    setEditSchema(results.schema);
    setEditFeature({ ...results.item });
    setEditFiles([...files]);
    setImportTitle(`Edit Observation (No:${item.No})`);
    setEditDialogOpen(true);
  };

  const onExportObservations = () => {
    try {
      const dataToExport = observations.map(({ Selected, ...rest }) => rest);
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Observations");
      XLSX.writeFile(workbook, `${project.Name}_Observations.xlsx`);
    } catch (error) {
      console.error("Error exporting Observations:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    }
  };

  const onEditDelete = async () => {
    try {
      setIsLoading(true);

      const savedProj = await onEditDeleteAll(
        project,
        editFeature,
        editFiles,
        "Observations",
        "observation_id",
        props.user,
      );

      setProject({ ...savedProj });
      setAllObservations([...savedProj.Observations]);
      onSortChange(sortByValue, [...savedProj.Observations]);
      setEditDialogOpen(false);
      enqueueSnackbar("Observation deleted successfully", {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "success",
      });
      setIsLoading(false);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
      setIsLoading(false);
    }
  };

  const onEditSave = async () => {
    try {
      //check for invalid
      var feat = { ...editFeature };
      var invalidResults = getFormInvalid(feat, editSchema);
      if (invalidResults.isInvalid === true) {
        setEditFeature({ ...invalidResults.feature });
        setEditSchema([...invalidResults.schema]);
        setformErrorMessage(invalidResults.formErrorMessage);
        return;
      }

      setIsLoading(true);

      const savedProj = await onEditSaveAll(
        project,
        feat,
        editSchema,
        editFiles,
        "Observations",
        "observation_id",
        props.user,
      );

      props.saveCurrentProject({ ...savedProj });
      setEditDialogOpen(false);
      setProject({ ...savedProj });
      setAllObservations([...savedProj.Observations]);
      onSortChange(sortByValue, [...savedProj.Observations]);
      enqueueSnackbar("Observation saved successfully", {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "success",
      });
      setIsLoading(false);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
      setIsLoading(false);
    }
  };

  const onEditChange = (e) => {
    try {
      var att = editSchema.find((item) => item.name === e.target.name);
      const formErrorMessage = checkInvalidField(e.target.value, att);
      if (att.type === "date" && e.target.value.length > 0) {
        var date = new Date(e.target.value);
        editFeature[e.target.name] = date.toISOString().split("T")[0];
        setEditFeature({ ...editFeature });
        setformErrorMessage(formErrorMessage);
      } else if (att.type === "bit") {
        editFeature[e.target.name] = e.target.checked;
        setEditFeature({ ...editFeature });
        setformErrorMessage(formErrorMessage);
      } else {
        editFeature[e.target.name] = e.target.value;
        setEditFeature({ ...editFeature });
        setformErrorMessage(formErrorMessage);
      }
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    }
  };

  const onEditFileCommentChange = (e) => {
    var file = editFiles.find((f) => f.id === e.target.id);
    file.comment = e.target.value;
    setEditFiles([...editFiles]);
  };

  const onRemoveEditFile = async (id) => {
    const updatedFiles = editFiles.map((obj) => {
      if (obj.id === id) {
        obj.deleted = true;
        return obj; // Replace the object if its ID matches
      }
      return obj; // Keep other objects as they are
    });

    setEditFiles([...updatedFiles]);
  };

  const getAcceptedFiles = async (files) => {
    try {
      const readyFiles = await processFiles(
        files,
        "observation_id",
        editFeature.id,
      );
      const allFiles = editFiles.concat(readyFiles);
      setEditFiles([...allFiles]);
    } catch (error) {
      console.error("Error getting document:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
        variant: "error",
      });
    }
  };

  const onEditCancel = () => {
    setformErrorMessage("");
    setEditDialogOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onSortChange = (value, array) => {
    if (value !== sortByValue) {
      setSortByValue(value);
    }
    const obs = getSortArray(value, array);
    setObservations([...obs]);
  };

  const onFilterChange = (searchTerm) => {
    if (searchTerm.length > 2) {
      const filteredData = allObservations.filter((obj) => {
        const values = Object.values(obj);
        return values.some((value) =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase()),
        );
      });

      setObservations([...filteredData]);
    } else {
      setObservations([...allObservations]);
    }
    setFilterValue(searchTerm);
    setPage(0);
  };

  const handleChecked = (e) => {
    const item = observations.find((item) => item.id === e.target.id);
    item.Selected = !item.Selected;
    setObservations([...observations]);
  };

  const onCloseImportDialog = () => {
    setImportDialogOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
    if (fileInputRef1.current) {
      fileInputRef1.current.value = null;
    }
    setSheetName(null);
    setImportTitle("Observations Import > Choose Worksheet");
    setImportSubtitle(`Choose the worksheet with the data you wish to import.`);
  };

  const onAttributeChange = (e) => {
    var fd = importFields.find((f) => f.name === e.target.name);
    fd.attribute = e.target.value;
    setImportFields([...importFields]);
  };

  const onFldChange = (e) => {
    var fd = importFields.find((f) => f.name === e.target.name);
    fd.selected = e.target.value;
    setImportFields([...importFields]);
  };

  const processExcel = (file) => {
    try {
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: "array" });

          setWorkBook(workbook);
          setImportTitle("Observations Import > Choose Worksheet");
          setImportDialogOpen(true);
        };
        reader.readAsArrayBuffer(file);
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    }
  };

  const onWorksheetSelected = async (e) => {
    try {
      const worksheet = workbook.Sheets[e];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      if (jsonData.length > 0) {
        const fields = mapSchema(jsonData, editSchema);
        setImportData(jsonData);
        setSheetName(e);
        setImportFields(fields);
        setImportTitle("Observations Import > Choose Fields");
        setImportSubtitle(
          `For each column of your spreadsheet, select the corresponding field. The "Description" field is required.`,
        );
      } else {
        throw new Error("Unable to import observations");
      }
    } catch (err) {
      throw new Error("Unable to import observations: " + err.message);
    }
  };

  const onImportExcel = async () => {
    try {
      if (importData.length === 0) {
        onCloseImportDialog();
        return;
      }

      const results = await getExcelFeatures(
        props.user,
        importData,
        importFields,
        editSchema,
        getObservationSchema()
          .filter(
            (fd) =>
              fd.name !== "id" &&
              fd.name !== "project_id" &&
              fd.nullable === false,
          )
          .map((fd) => fd.name),
        project,
        "Observations",
        null,
        null,
      );

      setImportStats([...getStats(results.newEquip)]);
      setImportSubtitle("Import Successful: Results shown below");
      setImportTitle("Observations Import > Complete");
      setImportFields([]);
      setFilterValue("");
      setPage(0);
      setProject({ ...results.project });
      props.saveCurrentProject({ ...results.project });
      onSortChange(sortByValue, [...results.freshFeatures]);
      setAllObservations([...results.freshFeatures]);
      setImportData([]);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
      // onCloseImportDialog();
    }
  };

  const getStats = (array) => {
    var importStat = [];
    importStat.push({ name: "Observations", count: array.length });
    return importStat;
  };

  const onUncheckSelected = () => {
    observations.forEach((item) => {
      if (item.Selected === true) {
        item.Selected = false;
      }
    });
    setObservations([...observations]);
  };

  const onDrawComplete = (id, pic) => {
    var file = editFiles.find((f) => f.id === id);
    if (file) {
      file.file = pic;
      if (!file.id.startsWith("new_")) {
        file.isDrawn = true;
      }
      setEditFiles([...editFiles]);
    }
  };

  return isLoading ? (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={isLoading}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  ) : (
    <Scrollbars
      autoHide
      autoHideTimeout={10}
      style={{ width: "100%", height: "100%" }}
    >
      <Toolbar />
      <Container
        maxWidth="xl"
        style={{
          // paddingTop: 30,
          paddingBottom: 30,
          // paddingBottom: theme.spacing(4),
          justifyContent: "center",
        }}
      >
        <Typography
          color="primary.light"
          style={{ paddingTop: 40 }}
          variant="h5"
          gutterBottom
        >
          Project Observations
        </Typography>

        <Stack
          sx={{
            justifyContent: "center",
            alignItems: "center",
          }}
          spacing={2}
        >
          <Breadcrumbs separator="›" aria-label="breadcrumb">
            <Link
              underline="hover"
              key="1"
              color="inherit"
              href=""
              onClick={() =>
                navigate(`/project/${project.id}/dashboard`, {
                  state: { passProject: project },
                })
              }
            >
              Project: {project.Name}
            </Link>
            ,
            <Typography
              underline="hover"
              key="2"
              color="inherit"
              href={`/project/${project.id}/observations`}

              // onClick={handleClick}
            >
              {`Observations (${allObservations.length})`}
            </Typography>
            ,
          </Breadcrumbs>
        </Stack>

        <Stack
          // style={{ paddingTop: 10 }}
          direction="row"
          spacing={2}
          sx={{
            justifyContent: "flex-end",
            alignItems: "flex-end",
          }}
        >
          <Tooltip title="Upload Observations">
            <IconButton
              color="primary"
              component="label"
              disabled={
                !getUserAccess(props.user, project.uid, project.Contacts)
                  .allowCreate
              }
              role={undefined}
              variant="contained"
              tabIndex={-1}
            >
              <VisuallyHiddenInput
                type="file"
                ref={fileInputRef}
                onChange={(event) =>
                  event.target.files.length > 0 &&
                  processExcel(event.target.files[0])
                }
              />
              <FileUploadIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Observation">
            <IconButton
              onClick={onAddNew}
              disabled={
                !getUserAccess(props.user, project.uid, project.Contacts)
                  .allowCreate
              }
              aria-label="Add Observation"
              size="large"
              color="primary"
            >
              <AddBoxIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download Observations">
            <IconButton
              onClick={onExportObservations}
              aria-label="Download Observations"
              disabled={observations.length === 0}
              size="large"
              color="primary"
            >
              <CloudDownloadIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack
          style={{ paddingTop: 30 }}
          sx={{
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {allObservations.length > 0 && (
            <SortDialog
              sortByValue={sortByValue}
              onSortChange={onSortChange}
              objArray={observations}
              sortByOptions={sortByOptions}
              filterValue={filterValue}
              onFilterChange={onFilterChange}
              page={page}
              handleChangePage={handleChangePage}
              rowsPerPage={rowsPerPage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          )}

          <Collapse
            style={{ width: "100%" }}
            in={observations.some((item) => item.Selected === true)}
          >
            <Paper elevation={6} style={{ width: "100%" }}>
              <Box
                sx={{
                  width: "100%",
                  height: 60,
                  borderRadius: 1,
                  bgcolor: "secondary.light",
                  // "&:hover": {
                  //   bgcolor: "primary.dark",
                  // },
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    justifyContent: "flex-start",
                    alignItems: "center",
                  }}
                  style={{ height: "100%", paddingLeft: 16 }}
                >
                  <Typography color="primary.contrastText">
                    {`Selected ${
                      observations.filter((f) => f.Selected === true).length
                    }`}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onUncheckSelected}
                    startIcon={<Unpublished />}
                  >
                    Uncheck All
                  </Button>
                </Stack>
              </Box>
            </Paper>
          </Collapse>

          {observations.length === 0 && allObservations.length === 0 ? (
            <Typography>
              There aren't any observations associated with this project. Please
              <Tooltip title="Upload Observations">
                <IconButton
                  color="primary"
                  component="label"
                  disabled={
                    !getUserAccess(props.user, project.uid, project.Contacts)
                      .allowCreate
                  }
                  role={undefined}
                  variant="contained"
                  tabIndex={-1}
                  size="medium"
                >
                  <VisuallyHiddenInput
                    type="file"
                    ref={fileInputRef1}
                    onChange={(event) =>
                      event.target.files.length > 0 &&
                      processExcel(event.target.files[0])
                    }
                  />
                  <FileUploadIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              upload or add
              <Tooltip title="Add Observation">
                <IconButton
                  onClick={onAddNew}
                  disabled={
                    !getUserAccess(props.user, project.uid, project.Contacts)
                      .allowCreate
                  }
                  aria-label="Add Observation"
                  size="medium"
                  color="primary"
                >
                  <AddBoxIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              new Observation
            </Typography>
          ) : observations.length === 0 && allObservations.length > 0 ? (
            <Typography>
              Search returned 0 results. Click here:
              <IconButton
                onClick={() => onFilterChange("")}
                aria-label="clear search"
                size="medium"
                color="primary"
              >
                <SearchOffIcon fontSize="inherit" />
              </IconButton>
              to clear search
            </Typography>
          ) : (
            <List style={{ width: "100%" }}>
              <ObservationCard
                observations={observations}
                projectId={project.id}
                page={page}
                rowsPerPage={rowsPerPage}
                onEdit={onEdit}
                allowUpdate={
                  getUserAccess(props.user, project.uid, project.Contacts)
                    .allowUpdate
                }
                onDetailsClick={onDetailsClick}
                handleChecked={handleChecked}
              />
            </List>
          )}
        </Stack>
        <ImportDialog
          isOpen={importDialogOpen}
          onClose={onCloseImportDialog}
          importTitle={importTitle}
          importSubtitle={importSubtitle}
          importData={importData}
          importFields={importFields}
          onFldChange={onFldChange}
          onAttributeChange={onAttributeChange}
          onImportExcel={onImportExcel}
          importStats={importStats}
          sheetName={sheetName}
          workbook={workbook}
          onWorksheetSelected={onWorksheetSelected}
        />
        <EditDialog
          allowDeleteAccess={
            getUserAccess(props.user, project.uid, project.Contacts).allowDelete
          }
          feature={editFeature}
          editFiles={editFiles}
          importTitle={importTitle}
          getAcceptedFiles={getAcceptedFiles}
          onEditFileCommentChange={onEditFileCommentChange}
          onRemoveEditFile={onRemoveEditFile}
          schema={editSchema}
          onChange={onEditChange}
          onEditSave={onEditSave}
          onEditDelete={onEditDelete}
          isOpen={editDialogOpen}
          onClose={onEditCancel}
          formErrorMessage={formErrorMessage}
          allowDelete={true}
          allowFiles={true}
          onDrawComplete={onDrawComplete}
        />
      </Container>
    </Scrollbars>
  );
}
